// src/pages/ProdutoDetalhe.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs, limit } from "firebase/firestore";
import { Helmet } from 'react-helmet';
import './ProdutoDetalhe.css';
import ContactForm from '../components/ContactForm';
import { useCart } from '../context/CartContext';

const formatPrice = (value) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    }).format(value || 0);
};

const ProdutoDetalhe = () => {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { addToCart } = useCart();

    useEffect(() => {
        const fetchProduct = async () => {
            if (!slug) return;
            setLoading(true);
            try {
                const productsRef = collection(db, "pts-com-br/fabricantes/intelbras_products");
                const q = query(productsRef, where("slug", "==", slug), limit(1));
                const querySnapshot = await getDocs(q);

                if (querySnapshot.empty) {
                    setError("Produto não encontrado.");
                } else {
                    setProduct({ id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() });
                }
            } catch (err) {
                setError("Erro ao buscar o produto.");
                console.error(err);
            }
            setLoading(false);
        };
        fetchProduct();
    }, [slug]);

    if (loading) {
        return <div className="container loading-state">Carregando produto...</div>;
    }
    if (error) {
        return <div className="container error-state">{error}</div>;
    }
    if (!product) {
        return null;
    }

    const productSchema = {
        "@context": "https://schema.org/",
        "@type": "Product",
        "name": product.nome,
        "image": product.mainImage,
        "description": product.resumo || product.nome,
        "sku": product.sku,
        "mpn": product.sku,
        "brand": {
            "@type": "Brand",
            "name": "Intelbras"
        },
        "offers": {
            "@type": "Offer",
            "url": `https://www.primotradesolutions.com.br/produtos/intelbras/${product.slug}`,
            "priceCurrency": "BRL",
            "price": product.precoSugerido.toFixed(2),
            "itemCondition": "https://schema.org/NewCondition",
            "availability": "https://schema.org/InStock",
            "seller": {
                "@type": "Organization",
                "name": "Primo Trade Solutions"
            },
            "hasMerchantReturnPolicy": {
                "@type": "MerchantReturnPolicy",
                "name": "Política de Devolução e Termos de Serviço",
                "url": "https://pts.dev.br/termos-e-condicoes", 
                "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
                "merchantReturnDays": 7, 
                "returnMethod": "https://schema.org/ReturnByMail",
                "returnFees": "https://schema.org/RestockingFees"
            },
            "shippingDetails": {
                "@type": "OfferShippingDetails",
                "shippingRate": {
                    "@type": "ShippingRateSpecification",
                    "priceCurrency": "BRL",
                    "price": "0.00", 
                    "isBasedOn": ["http://schema.org/Distance", "http://schema.org/Weight"]
                },
                "shippingDestination": {
                    "@type": "DefinedRegion",
                    "addressRegion": "SP",
                    "addressCountry": "BR"
                },
                "deliveryTime": {
                    "@type": "ShippingDeliveryTime",
                    "handlingTime": {
                        "@type": "QuantitativeValue",
                        "minValue": 0,
                        "maxValue": 1,
                        "unitCode": "DAY"
                    },
                    "transitTime": {
                        "@type": "QuantitativeValue",
                        "minValue": 1,
                        "maxValue": 5,
                        "unitCode": "DAY"
                    }
                }
            }
        }
    };

    return (
        <div className="page-produto-detalhe">
            <Helmet>
                <title>{`${product.nome} | Catálogo Intelbras PTS`}</title>
                <meta name="description" content={product.resumo || `Detalhes e especificações do produto ${product.nome}. Solicite um orçamento de instalação com a PTS.`} />
                <script type="application/ld+json">
                    {JSON.stringify(productSchema)}
                </script>
            </Helmet>

            <div className="container">
                <div className="breadcrumb">
                    <Link to="/produtos/intelbras">Catálogo Intelbras</Link> / <span>{product.nome}</span>
                </div>
                <div className="produto-detalhe-layout">
                    <div className="produto-imagem-col">
                        <img src={product.mainImage} alt={product.nome} />
                    </div>
                    <div className="produto-info-col">
                        <h1>{product.nome}</h1>
                        <span className="sku-detalhe">SKU: {product.sku}</span>
                        <p className="resumo-detalhe">{product.resumo}</p>
                        <div className="preco-sugerido">{formatPrice(product.precoSugerido)}</div>
                        
                        {/* /// INÍCIO DO BLOCO DE TEXTO UNIFICADO /// */}
                        <div className="projeto-completo-box">
                            <h4>Não Compre Apenas Produtos. Invista em um Projeto Completo.</h4>
                            <p>
                                Cuidamos de todo o seu projeto de segurança e automação Intelbras, do planejamento à instalação. Atendemos residências, condomínios e empresas com a mesma excelência. A solução certa, instalada por quem entende, é o que garante sua tranquilidade.
                            </p>
                            <p className="cta-final">
                                <strong>Cansado de listas de compras?</strong> Pule essa etapa. Vá até nosso formulário de contato, explique seu projeto e deixe o trabalho pesado conosco. Solicite seu orçamento completo.
                            </p>
                        </div>
                        {/* /// FIM DO BLOCO DE TEXTO UNIFICADO /// */}

                        <button onClick={() => addToCart(product)} className="cta-orcamento">
                            Adicionar à Cotação
                        </button>
                    </div>
                </div>

                <div className="especificacoes-tecnicas">
                    <h2>Especificações Técnicas</h2>
                    <div className="specs-grid">
                        <div className="spec-item"><strong>Unidade:</strong> <span>{product.unidade}</span></div>
                        <div className="spec-item"><strong>Segmento:</strong> <span>{product.segmento}</span></div>
                        <div className="spec-item"><strong>Família:</strong> <span>{product.familia}</span></div>
                        <div className="spec-item"><strong>EAN:</strong> <span>{product.ean || 'N/D'}</span></div>
                        <div className="spec-item"><strong>NCM:</strong> <span>{product.infoFiscal?.ncm || 'N/D'}</span></div>
                        <div className="spec-item"><strong>Peso:</strong> <span>{product.infoLogistica?.peso} kg</span></div>
                        <div className="spec-item"><strong>Altura:</strong> <span>{product.infoLogistica?.alt} cm</span></div>
                        <div className="spec-item"><strong>Largura:</strong> <span>{product.infoLogistica?.larg} cm</span></div>
                        <div className="spec-item"><strong>Comprimento:</strong> <span>{product.infoLogistica?.comp} cm</span></div>
                    </div>
                </div>
            </div>
            
            <div id="contato-form">
                <ContactForm />
            </div>
        </div>
    );
};

export default ProdutoDetalhe;